import { Link } from 'react-router';
import { CouncilClaimIntro } from '@/components/councils/council-claim-intro';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';
import { SmartCTA } from '@/components/ui/smart-cta';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Dudley\'s AMBER overall DfT rating mean I cannot claim?',
    answer: 'No. The England-only DfT bands are network-level context: AMBER overall and condition, GREEN spend and RED best practice. The outcome of an individual claim depends on the specific incident and evidence; the ratings do not establish notice, breach or liability.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Dudley\'s July 2025 transparency report records 853.4km of unclassified roads and a four-year condition-survey cycle, with 25% surveyed each year. These network figures do not show when a particular street was safety-inspected or what was known about a specific defect; request the location records.',
  },
  {
    question: 'Dudley maintains A and B roads well — does that weaken my claim?',
    answer: 'The report records A and B/C roads at 86/100 with roughly 2% in poor condition. Those network metrics do not determine the condition, inspection history or liability for the incident location.',
  },
  {
    question: 'What is the Daytime Blitz and why does it matter?',
    answer: 'Dudley\'s July 2025 transparency report describes a £1.3 million "Daytime Blitz" targeting around 30,000 square metres on the unclassified network and says estate roads are "sometimes overlooked". That is programme-level context, not evidence of notice, breach or liability for a specific defect.',
  },
  {
    question: 'Does the RED Best Practice scorecard decide my claim?',
    answer: 'No. The DfT Best Practice band is separate from condition and spend and is network-level context only. It does not establish what Dudley knew about a specific defect or determine a Section 58 defence.',
  },
  {
    question: 'Dudley has a GREEN Spend scorecard — can I still claim?',
    answer: 'Yes. GREEN spend reflects aggregate programme information and does not decide an individual claim. Use photographs and request the authority\'s report, inspection and repair records for the location.',
  },
  {
    question: 'How do I report a pothole to Dudley Metropolitan Borough Council?',
    answer: 'Report defects via Dudley Council\'s online form and keep the acknowledgement. A FixMyStreet entry is a lead to verify, not proof Dudley received or matched it to the same defect.',
  },
];

export function DudleyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Dudley Council Pothole Claims: How to Claim | Fixtyer"
        description="How to make a Dudley Council pothole damage claim: official claim instructions, evidence checklist, free letter template and local road-maintenance data."
        canonical="/councils/dudley"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Dudley', url: '/councils/dudley' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Dudley Metropolitan Borough Council"
        councilSlug="dudley"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/dudley"
        headline="Dudley Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Dudley</span>
          </nav>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Dudley pothole claims: how to claim
          </h1>
          <CouncilClaimIntro area="Dudley" />
          <h2 className="font-display text-2xl font-bold mb-5">Dudley road maintenance: the evidence</h2>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Dudley is AMBER overall and for condition, GREEN for spend and RED for best practice in the
            England-only DfT ratings.
            These network-level bands do not establish notice, breach or liability for a particular pothole.
          </p>
          <p className="mb-6 text-sm font-medium text-brand-200">Last reviewed: 19 July 2026</p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">25%</div>
            <div className="text-white font-medium mt-2">U-road condition-survey coverage each year</div>
            <div className="text-brand-300 text-sm mt-1">
              On 853.4km of residential and estate roads — a four-year cycle meaning roughly three-quarters of the
              unclassified network may not be condition-surveyed in any given year. This does not state the frequency
              of safety inspections.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the DfT Scorecards Actually Show
          </h2>
          <p className="text-surface-600 mb-8">
            Four separate scorecards for 2025/26 — condition metrics and programme ratings are not the same thing
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Dudley Metropolitan Borough Council DfT Road Maintenance Ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">What it reflects</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200 text-sm">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-surface-600">Combined assessment across condition, spend and best practice</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-surface-600">Network-wide road condition — dragged down by U-road scores</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-surface-600">Capital and revenue investment relative to DfT expectations</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                  <td className="px-6 py-4 text-surface-600">Maintenance programme standards — inspection regimes, prevention vs reaction</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Dudley DfT condition metrics by road class</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Condition score</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">% in poor condition</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">A-roads (94.7km)</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">86/100</td>
                  <td className="px-6 py-4 text-center text-emerald-600">2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">B/C roads (85.1km)</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">86/100</td>
                  <td className="px-6 py-4 text-center text-emerald-600">2%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium">U-roads (853.4km)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">42/100</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <p className="text-amber-800 text-sm">
              <strong>Source caveat:</strong> DfT scorecards assess the 2025/26 reporting period. Condition scores
              are network-level metrics derived from council survey data — they may not reflect the state of your
              specific road at the time of your incident. Dudley's July 2025 transparency report uses its own survey
              methodology; figures below come from that report unless labelled as DfT scorecards.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network divide */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,033.2km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network breakdown from Dudley's July 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,033.2km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">853.4km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">82.6% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">94.7km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">85.1km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Dudley highway network breakdown by road class</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road type</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">% of network</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">% in poor condition</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">A-roads</td>
                  <td className="px-6 py-4 text-center">94.7 km</td>
                  <td className="px-6 py-4 text-center">9.2%</td>
                  <td className="px-6 py-4 text-center text-emerald-600 font-medium">2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">B/C roads</td>
                  <td className="px-6 py-4 text-center">85.1 km</td>
                  <td className="px-6 py-4 text-center">8.2%</td>
                  <td className="px-6 py-4 text-center text-emerald-600 font-medium">2%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium">U-roads</td>
                  <td className="px-6 py-4 text-center font-bold">853.4 km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">82.6%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Classified roads — where investment landed</h3>
              <p className="text-emerald-800 text-sm">
                Dudley received £9.2 million (2016) and £3.9 million (2019) in Challenge Fund grants specifically for
                classified roads. A and B/C routes now score 86/100 with just 2% in poor condition — but classified
                roads are under 18% of the carriageway network.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">U-roads — where most journeys happen</h3>
              <p className="text-rose-800 text-sm">
                School runs, estate streets and local shopping trips use unclassified roads. At 18% poor condition,
                that is roughly 154km of U-road network the council itself classifies as needing maintenance — on roads
                condition-surveyed on a four-year cycle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Dudley&apos;s network condition surveys and location-specific safety inspections are different records
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">B and C roads</h3>
              <div className="text-3xl font-bold text-emerald-400 mb-2">100%</div>
              <p className="text-brand-300 text-sm">Condition-surveyed in both directions over a two-year period</p>
              <p className="text-brand-400 text-xs mt-1">Equivalent to 50% condition-surveyed annually</p>
            </div>
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">U-roads (853.4km — 82.6% of network)</h3>
              <div className="text-3xl font-bold text-amber-400 mb-2">25%</div>
              <p className="text-brand-300 text-sm">Condition-surveyed each year on a four-year cycle</p>
              <p className="text-brand-400 text-xs mt-1">Roughly three-quarters not condition-surveyed in any given year</p>
            </div>
          </div>

          <div className="bg-brand-900/30 rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Section 41 of the Highways Act 1980 concerns the duty to maintain public highways. If Dudley relies on
              <strong className="text-white"> Section 58</strong>, records that may be relevant to a specific incident
              include its safety-inspection policy, completed inspections, defect reports, risk assessments and repair
              history for the location. Network condition scores do not answer those questions.
            </p>
            <p className="text-brand-200 mb-4">
              <strong className="text-white">Condition surveys do not replace safety inspections.</strong> Dudley&apos;s
              four-year condition-survey cycle describes network monitoring; it is not evidence of the safety-inspection
              interval for a particular road and does not determine whether a Section 58 defence is established.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• The applicable safety-inspection policy and completed records for the road</li>
              <li>• Any recorded defect assessment and intervention criteria applied at the location</li>
              <li>• Dudley&apos;s own logs for reports concerning the same defect, including receipt and status</li>
              <li>• Risk assessments, repair instructions and completed repair records for the relevant period</li>
            </ul>
          </div>

          <QuoteCard
            quote="The minor side roads can create an enormous volume of defect reports which need to be inspected and categorised for severity"
            source="Dudley Council Local Highways Maintenance Transparency Report (July 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend, RED Best Practice
          </h2>
          <p className="text-surface-600 mb-8">
            Maintenance spending 2025/26 from Dudley's transparency report — prevention vs reaction
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£16m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total highways budget</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">£10.76m</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Capital spend</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£5.23m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Revenue spend</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">3.67×</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Reactive vs preventative</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <div className="text-4xl font-bold text-rose-600">6%</div>
              <div className="text-rose-800 font-medium">Preventative maintenance</div>
              <p className="text-sm text-rose-700 mt-2">Treatments that stop potholes forming</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="text-4xl font-bold text-amber-600">22%</div>
              <div className="text-amber-800 font-medium">Reactive maintenance</div>
              <p className="text-sm text-amber-700 mt-2">Fixing potholes after they form</p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why Best Practice is RED</h3>
            <p className="text-brand-200 mb-4">
              For every £1 spent preventing potholes, Dudley spends £3.67 fixing them after they form. The council's
              own report quotes HMEP guidance:
            </p>
            <p className="text-white italic mb-4">
              "Prevention is better than cure — intervening at the right time will reduce the number of potholes
              forming and prevent bigger problems later"
            </p>
            <p className="text-amber-400 font-medium">
              Yet 2025/26 budgets allocate 6% to prevention and 22% to reaction — a pattern the DfT Best Practice
              scorecard rates RED.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            5,758 Potholes Filled in 2022/23 — Then the Daytime Blitz
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled from Dudley's transparency report — reactive workload on the unclassified network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Dudley estimated potholes filled 2020 to 2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Note</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,507</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,209</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">5,758</td>
                  <td className="px-6 py-4 text-center text-amber-600">Peak year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,564</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,706</td>
                  <td className="px-6 py-4 text-center text-surface-500">Latest reported year</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-3">The Daytime Blitz</h3>
            <QuoteCard
              quote="A Daytime Blitz is also underway on the Unclassified network but predominantly estate roads which are sometimes overlooked but we recognise that the minor side roads can create an enormous volume of defect reports which need to be inspected and categorised for severity, so the Daytime Blitz works is attacking this problem head-on."
              source="Dudley Council Local Highways Maintenance Transparency Report (July 2025)"
            />
            <p className="text-amber-800 text-sm mt-4">
              The council plans to spend £1.3m on the programme, targeting around 30,000 square metres of potholes on
              the unclassified network. This is programme-level context and does not show what Dudley knew about
              a specific defect.
            </p>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">U-road condition trend</h3>
            <p className="text-surface-600 text-sm">
              Dudley's report shows the percentage of U-roads in poor (RED) condition falling from 29% in 2020 to 18%
              in 2025 — improvement, but 18% of 853.4km is still roughly 154km of residential network in poor
              condition, on roads condition-surveyed only 25% each year. Improvement at network level does not prove your specific
              street was maintained to a reasonable standard.
            </p>
          </div>
        </div>
      </section>

      {/* Council statements */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Dudley&apos;s Report States
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim statements from the July 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On neglected estate roads</h3>
              <QuoteCard
                quote="The Unclassified network but predominantly estate roads which are sometimes overlooked"
                source="Dudley Council Local Highways Maintenance Transparency Report (July 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On prevention vs cure</h3>
              <QuoteCard
                quote="Prevention is better than cure — intervening at the right time will reduce the number of potholes forming and prevent bigger problems later"
                source="HMEP Guidance, cited in Dudley Council Local Highways Maintenance Transparency Report (July 2025)"
              />
              <p className="text-surface-600 mt-3 text-sm">
                The council cites this principle while budgeting 6% for prevention and 22% for reactive repairs in
                2025/26 — a disconnect the DfT Best Practice scorecard rates RED.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Assessing Dudley&apos;s Network Data
          </h2>
          <p className="text-surface-600 mb-8">
            Network-level strengths and weaknesses to keep separate from the evidence for a specific incident
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council's favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B/C roads at 86/100 with 2% poor condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN Spend scorecard — £10.76m capital budget projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Challenge Fund investment on classified routes (£9.2m in 2016, £3.9m in 2019)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road poor-condition share falling from 29% to 18% over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C roads condition-surveyed 100% in both directions over two years
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                The authority may rely on these records, but the evidence for the specific location still needs to be assessed.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">Records to examine</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED Best Practice scorecard from the DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">•</span> 853.4km of U-roads — 82.6% of network — on a four-year condition-survey cycle, not a stated safety-inspection interval
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road condition score 42/100; 18% still in poor condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">•</span> The report says estate roads are "sometimes overlooked"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £1.3m Daytime Blitz to catch up on unclassified backlog
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 6% prevention vs 22% reactive spend — 3.67× reactive ratio
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Keep the assessment location-specific</h3>
            <p className="text-brand-200 mb-4">
              Dudley&apos;s network ratings do not decide an individual claim. Review evidence about the{' '}
              <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Dudley&apos;s own logs confirming whether and when the same defect was reported</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class and the applicable safety-inspection records, not condition surveys alone</li>
              <li>• Council statements and the Daytime Blitz used only as programme-level context</li>
            </ul>
            <p className="text-white font-medium">
              Mac can organise the information you provide and identify third-party report leads for you to verify.
              Neither the DfT ratings nor those leads establish notice or liability.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Dudley Metropolitan Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Report a defect through Dudley&apos;s portal and retain its acknowledgement. A third-party report alone
            does not prove Dudley received it.
          </p>
          <a
            href="https://www.dudley.gov.uk/residents/parking-and-roads/street-care-and-maintenance/report-a-pothole/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — dudley.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Dudley?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A two-tier network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• Find Dudley&apos;s location-specific records yourself</li>
                <li>• Check third-party report leads yourself</li>
                <li>• Keep network ratings in their proper context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road 42/100 condition score labelled as network context</li>
                <li>✅ Condition surveys kept separate from safety inspections</li>
                <li>✅ RED Best Practice and Daytime Blitz sources identified</li>
                <li>✅ Third-party report leads identified for verification</li>
                <li>✅ Section 58 records organised for your review</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <SmartCTA
              guestText="Order My Dudley Claim Pack — £35"
              guestPath="/check?council=dudley"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
              showArrow
            />
            <Link
              to="/authority-ratings"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              Search all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.dudley.gov.uk/residents/parking-and-roads/street-care-and-maintenance/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Dudley Council Local Highways Maintenance Transparency Report (July 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
